/* eslint-disable react/prop-types */
import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import {
  getSession,
  getWorkflowByCampaign,
  joinCampaignWorkflow,
  createWorkflowFromCampaign,
  transformBlocksToFrontend,
  extractFileText,
} from "@/utils/maaran";
import toast from "react-hot-toast";
import useLicenseActive from "@/hooks/useLicenseActive";

import { useDispatch } from "react-redux";
import {
  resetPreviewState,
  setData,
  setPreviewOpen,
  setType,
} from "@/store/assistance";
import { toggleModal } from "@/store/task";
import useTokenTracking from "@/hooks/ai-mode/useTokenTracking";
import useSendTranscribe from "@/hooks/ai-mode/useSendTranscribe";
import useMaaranChat from "@/hooks/ai-mode/useMaaranChat";
import useWorkflow from "@/hooks/ai-mode/useWorkflow";
import useGenerateStepOutput from "@/hooks/ai-mode/useGenerateStepOutput";
import useResumeWorkflow from "@/hooks/ai-mode/useResumeWorkflow";
import { formatStepIdForMoreHumanReadable } from "@/utils/helper";
import { setChangedAIMode } from "@/store/common";

const DEFAULT_MODE = "workflow";

/**
 * Derive a user-friendly status message from the user's input text.
 * Shown in the thinking indicator while the backend processes the request.
 */
function deriveStatusMessage(userMsg) {
  if (!userMsg) return "";
  const msg = userMsg.toLowerCase().trim();

  if (/stats|statistic|performance|report/.test(msg)) return "Fetching data...";
  if (/template|playbook/.test(msg)) return "Searching templates...";
  if (/campaign|workflow/.test(msg) && /create|launch|new/.test(msg))
    return "Sure! Finding you the relevant workflow templates";
  if (/campaign|workflow/.test(msg)) return "Loading workflows...";
  if (/blog|article|post|write|content/.test(msg))
    return "Generating content...";
  if (/email|newsletter/.test(msg)) return "Drafting email...";
  if (/brand/.test(msg)) return "Loading brand details...";
  if (/task/.test(msg)) return "Loading tasks...";
  if (/search|find|look/.test(msg)) return "Searching...";
  if (/analy|compet|audit|review/.test(msg)) return "Analysing...";

  return "Thinking...";
}

const resolveModeFromSession = (sessionData) => {
  return (
    sessionData?.metadata?.workflow_state?.mode ||
    sessionData?.workflow?.mode ||
    "agents"
  );
};

const AssistanceContext = createContext();

export default function AssistanceProvider({ children }) {
  // HOOKS
  const router = useRouter();
  const reduxDispatch = useDispatch();
  const queryClient = useQueryClient();
  const [messages, dispatch] = useReducer(messageReducer, []);
  const isLicenseActive = useLicenseActive();
  const selectedBrand = useSelector((state) => state?.common?.brand);
  const { isBlocked, trackTokens } = useTokenTracking();
  const changedAiMode = useSelector((state) => state.common.changedAiMode);
  const selectedBrandId = selectedBrand?.value || null;

  // STATES
  const [hasStarted, setHasStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecutingTask,setIsExecutingTask] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [aiMode, setAiMode] = useState(DEFAULT_MODE);
  const [agent, setAgent] = useState(null);

  const [sessionId, setSessionId] = useState(null);
  const [sessionBrandId, setSessionBrandId] = useState(null);
  const [sessionBrandKnown, setSessionBrandKnown] = useState(false);

  const [input, setInput] = useState("");

  const [workflowId, setWorkflowId] = useState(null);
  const [workflowCampaignId, setWorkflowCampaignId] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState("");
  const [hitlConfirmations, setHitlConfirmations] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [regenerateStep, setRegenerateStep] = useState(null);
  const [openChangeModeConfirmationAlert, setOpenChangeModeConfirmationAlert] =
    useState(false);
  const [pendingStatusMessage, setPendingStatusMessage] = useState("");

  // API HOOKS
  const { chat, handleConfirmation, isPending, isResponding, error } =
    useMaaranChat();
  const { transcribeAudio, isTranscribing } = useSendTranscribe();
  const { data: workflowData, isLoading: isWorkflowLoading } =
    useWorkflow(workflowId);
  const { mutateAsync: generateStepOutput, isPending: isGeneratingOutput } =
    useGenerateStepOutput();
  const { mutateAsync: resumeWorkflow, isPending: isResumingWorkflow } =
    useResumeWorkflow();

  const refreshSessions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
  }, [queryClient]);

  const resetConversationState = useCallback(
    ({ clearUrl = true } = {}) => {
      dispatch({ type: "CLEAR_MESSAGES" });
      setSessionId(null);
      setHasStarted(false);
      setSessionBrandId(null);
      setSessionBrandKnown(false);
      setAiMode(DEFAULT_MODE);
      setAgent(null);
      setPendingConfirmation(null);
      setRegenerateStep(null);
      setWorkflowCampaignId(null);
      setShouldScrollToBottom(true);
      setOpenChangeModeConfirmationAlert(false);
      reduxDispatch(resetPreviewState());
      if (clearUrl) {
        router.replace("/ai-mode", undefined, { shallow: true });
      }
    },
    [router],
  );

  const registerNewSession = useCallback(
    (newSessionId, resolvedBrandId) => {
      if (!newSessionId) return;
      setSessionId(newSessionId);
      setSessionBrandId(resolvedBrandId ? String(resolvedBrandId) : null);
      setSessionBrandKnown(true);
      router.replace(`/ai-mode?sessionId=${newSessionId}`, undefined, {
        shallow: true,
      });
      refreshSessions();
    },
    [refreshSessions, router],
  );

  const setMode = useCallback(
    async (nextMode) => {
      if (!nextMode || nextMode === aiMode) return;

      if (sessionId) {
        // ask confirmation
        setOpenChangeModeConfirmationAlert(true);
        reduxDispatch(setChangedAIMode(nextMode));
      } else {
        setAiMode(nextMode);
      }
    },
    [sessionId, aiMode],
  );

  const createUserMessage = useCallback((input, attachments) => {
    // Store serializable attachment metadata (not raw File objects) so they survive refresh
    const serializableAttachments = (attachments || []).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      // File objects can't be serialized — store just metadata for display
    }));
    const userPayload = {
      id: crypto.randomUUID(),
      role: "user",
      content: input,
      attachments:
        serializableAttachments.length > 0
          ? serializableAttachments
          : undefined,
      blocks: [],
      actions: [],
      suggestions: [],
    };

    dispatch({ type: "ADD_MSG_USER", payload: userPayload });
  }, []);

  const convertToText = useCallback(async (userMsg) => {
    const formData = new FormData();
    const fileOfBlob = new File([userMsg], "audio.mp4", {
      type: userMsg.type || "audio/mp4",
    });
    formData.append("file", fileOfBlob);
    const response = await transcribeAudio(formData);
    return response.text;
  }, []);

  const addHitlMessage = useCallback(
    (response) => {
      const confirmationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          response.confirmation.message || "Action requires your approval.",
        blocks: [
          {
            kind: "markdown",
            text:
              response.confirmation.message || "Action requires your approval.",
          },
          {
            kind: "ui_component",
            name: "hitl_confirmation",
            props: {
              confirmationId: response.confirmation.confirmation_id,
              toolName: response.confirmation.tool_name,
              toolArguments: response.confirmation.tool_arguments,
              expiresAt: response.confirmation.expires_at,
            },
          },
        ],
        actions: [],
        suggestions: [],
        confirmation: response.confirmation,
      };
      dispatch({ type: "ADD_MSG_ASSISTANT", payload: confirmationMessage });
    },
    [dispatch],
  );

  const handleMsgSubmit = useCallback(
    async (userMsg, attachments = [], contentType = "text", options = {}) => {
      if (isBlocked) {
        return;
      }

      if (!isLicenseActive) {
        toast("Please activate your license to continue using our services");
        return;
      }

      const forceNewSession = Boolean(options?.forceNewSession);
      if (forceNewSession) {
        resetConversationState({ clearUrl: true });
      }

      let userInput = userMsg;

      if (contentType === "audio") {
        userInput = convertToText(userMsg);
      }

      createUserMessage(userInput, attachments);

      // Derive status message — use workflow step name when in workflow mode
      let statusMsg = deriveStatusMessage(userInput);
      if (
        aiMode === "workflow" &&
        workflowData?.current_step &&
        workflowData?.step_cards
      ) {
        try {
          const [phaseIdx, goalIdx] = workflowData.current_step
            .split(":")
            .map(Number);
          const phase = workflowData.step_cards.phases?.[phaseIdx];
          const goal = phase?.goals?.[goalIdx];
          const step = goal?.steps?.find(
            (s) => s.id === workflowData.current_step,
          );
          if (step?.name) {
            statusMsg = `Executing: ${step.name}...`;
          }
        } catch {
          /* fallback to derived message */
        }
      }
      setPendingStatusMessage(statusMsg);

      if (!hasStarted) setHasStarted(true);

      if (regenerateStep) {
        await generateOutput(regenerateStep, userMsg);
        return;
      }

      try {
        // Read file contents from attachments for the backend
        let processedAttachments = [];
        if (attachments && attachments.length > 0) {
          const TEXT_TYPES = [
            "text/plain",
            "text/csv",
            "text/markdown",
            "application/json",
            "text/html",
            "text/xml",
            "application/xml",
          ];
          const TEXT_EXTENSIONS = [
            ".txt",
            ".csv",
            ".md",
            ".json",
            ".html",
            ".xml",
            ".log",
            ".yml",
            ".yaml",
          ];

          processedAttachments = await Promise.all(
            attachments.map(async (file) => {
              const ext = (file.name || "")
                .substring(file.name.lastIndexOf("."))
                .toLowerCase();
              const isText =
                TEXT_TYPES.includes(file.type) || TEXT_EXTENSIONS.includes(ext);

              if (isText) {
                // Text files — read directly on client
                return new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const content = e.target.result;
                    const truncated =
                      content.length > 50000
                        ? content.substring(0, 50000) +
                          "\n\n[... truncated, file too large to include fully]"
                        : content;
                    resolve({
                      name: file.name,
                      type: file.type || "text/plain",
                      size: file.size,
                      content: truncated,
                    });
                  };
                  reader.onerror = () =>
                    resolve({
                      name: file.name,
                      type: file.type,
                      size: file.size,
                      content: null,
                    });
                  reader.readAsText(file);
                });
              }

              // All other files (PDF, DOCX, images, etc.) — process via backend
              try {
                console.log(
                  "[Attachments] Processing binary file via backend:",
                  file.name,
                  file.type,
                );
                const result = await extractFileText(
                  file,
                  selectedBrandId ? String(selectedBrandId) : undefined,
                  workflowCampaignId || undefined,
                );
                console.log(
                  "[Attachments] Backend result:",
                  file.name,
                  "content length:",
                  result?.content?.length,
                );
                return {
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  content: result.content,
                  url: result.url || null,
                  asset_id: result.asset_id || null,
                };
              } catch (err) {
                console.warn("File processing failed:", file.name, err.message);
                return {
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  content: `[Could not process file: ${err.message}]`,
                };
              }
            }),
          );
        }

        // Send message to MAARAN
        const resolvedBrandId =
          options?.brandId ??
          (selectedBrandId ? String(selectedBrandId) : undefined);
        const mergedMetadata = {
          ...(selectedBrandId ? { brand_id: String(selectedBrandId) } : {}),
          ...(options?.metadata || {}),
        };
        if (resolvedBrandId) {
          mergedMetadata.brand_id = String(resolvedBrandId);
        }
        const routedAgent = agent?.name;
        if (
          routedAgent &&
          aiMode === "agents" &&
          !mergedMetadata.routed_agent
        ) {
          mergedMetadata.routed_agent = routedAgent;
        }

        const activeSessionId = forceNewSession ? null : sessionId;
        const {
          response,
          sessionId: newSessionId,
          message,
          isAwaitingConfirmation: needsConfirmation,
        } = await chat(userInput, activeSessionId, {
          brandId: resolvedBrandId,
          mode: aiMode,
          metadata:
            Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
          attachments:
            processedAttachments.length > 0 ? processedAttachments : undefined,
        });

        trackTokens(response.total_tokens);

        // Update session ID if new session was created
        if (!activeSessionId && newSessionId) {
          registerNewSession(newSessionId, resolvedBrandId);
        }

        if (needsConfirmation && response.confirmation) {
          setPendingConfirmation({
            ...response.confirmation,
            sessionId: newSessionId || sessionId,
          });
          addHitlMessage(response);
        } else {
          dispatch({ type: "ADD_MSG_ASSISTANT", payload: message });
        }

        if (
          userMsg == "pause workflow" ||
          userMsg == "pause the  workflow" ||
          userMsg == "resume workflow" ||
          userMsg == "resume the workflow"
        ) {
          queryClient.invalidateQueries(["workflow"]);
        }

        if (response?.metadata?.workflow_id) {
          setAiMode("workflow");
          // Keep workflowId in sync so useWorkflow always has fresh data.
          const resolvedWfId =
            response.metadata.workflow_instance_id ||
            response.metadata.workflow_id;
          if (resolvedWfId) {
            setWorkflowId(resolvedWfId);
            queryClient.invalidateQueries({
              queryKey: ["workflow", resolvedWfId],
            });
          }
        }
        if (response?.metadata?.campaign_id) {
          setWorkflowCampaignId(String(response.metadata.campaign_id));
        }
      } catch (err) {
        console.error("Chat error:", err);

        // Build user-friendly error message based on error type
        let userMessage;
        if (err.status === 408 || err.name === "AbortError") {
          userMessage =
            "Request timed out. The server might be busy — please try again.";
        } else if (err.status === 0 || !navigator.onLine) {
          userMessage =
            "Unable to connect. Please check your internet connection.";
        } else if (err.status === 401) {
          userMessage = "Your session has expired. Please refresh the page.";
        } else if (err.status >= 500) {
          userMessage =
            "Something went wrong on our end. Please try again in a moment.";
        } else {
          userMessage =
            err.message || "Sorry, something went wrong. Please try again.";
        }

        // Add error message
        const errorMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: userMessage,
          blocks: [
            {
              kind: "markdown",
              text: `**Error:** ${userMessage}`,
            },
          ],
          actions: [],
          suggestions: [],
          isError: true,
        };
        dispatch({ type: "ADD_MSG_ASSISTANT", payload: errorMessage });
      } finally {
        setPendingStatusMessage("");
      }
      setShouldScrollToBottom(false);
    },
    [
      aiMode,
      agent,
      chat,
      hasStarted,
      isBlocked,
      isLicenseActive,
      registerNewSession,
      resetConversationState,
      selectedBrandId,
      sessionId,
      transcribeAudio,
    ],
  );

  const handleConfirmationResponse = useCallback(
    async (action, override = null) => {
      const confirmationId =
        override?.confirmationId ||
        override?.confirmation_id ||
        pendingConfirmation?.confirmation_id;
      const confSessionId =
        override?.sessionId ||
        override?.session_id ||
        pendingConfirmation?.sessionId;
      const currentSessionId = confSessionId || sessionId;

      if (!confirmationId || !currentSessionId) {
        console.warn("No pending confirmation to respond to");
        return;
      }

      try {
        const { response, message } = await handleConfirmation(
          currentSessionId,
          confirmationId,
          action,
        );

        setPendingConfirmation(null);

        dispatch({ type: "ADD_MSG_ASSISTANT", payload: message });

        const responseMode = response?.metadata?.mode;

        if (responseMode && responseMode !== aiMode) {
          setAiMode(responseMode);
        } else {
          if (response?.metadata?.workflow_id) {
            const resolvedWfId =
              response.metadata.workflow_instance_id ||
              response.metadata.workflow_id;
            if (resolvedWfId) {
              setWorkflowId(resolvedWfId);
              queryClient.invalidateQueries({
                queryKey: ["workflow", resolvedWfId],
              });
            }
            reduxDispatch(
              setData({
                workflow: {
                  id: resolvedWfId,
                },
              }),
            );
            reduxDispatch(setType("workflow"));
            setTimeout(()=>{
              reduxDispatch(setPreviewOpen(true));
            },1000);

            setAiMode("workflow");
            // add this messages.
            const successMessage = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: ``,
              blocks: [
                {
                  kind: "markdown",
                  text: ` I have created your workflow. Click on Start Workflow to begin when you are ready`,
                },
              ],
              actions: [],
              suggestions: [],
              isError: true,
            };
            dispatch({ type: "ADD_MSG_ASSISTANT", payload: successMessage });
            toast.success('Workflow Tasks are assigned to you.')
          }
        }
      } catch (err) {
        console.error("Confirmation response error:", err);

        const errorMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Failed to ${action} the action. Please try again.`,
          blocks: [
            {
              kind: "markdown",
              text: `**Error:** Failed to ${action} the action. ${err.message}`,
            },
          ],
          actions: [],
          suggestions: [],
          isError: true,
        };
        dispatch({ type: "ADD_MSG_ASSISTANT", payload: errorMessage });
      }
    },
    [handleConfirmation, messages, pendingConfirmation, sessionId],
  );

  const startNewConversation = useCallback(() => {
    resetConversationState({ clearUrl: true });
  }, [resetConversationState]);

  const loadSession = useCallback(
    (existingSessionId, existingMessages = []) => {
      setSessionId(existingSessionId);
      if (existingMessages.length > 0) {
        dispatch({ type: "SET_MESSAGES", payload: existingMessages });
        setHasStarted(true);
      }
    },
    [],
  );

  /**
   * Handle URL-based session loading
   * When sessionId changes in URL, load that session
   */
  useEffect(() => {
    const urlSessionId = router.query.sessionId;

    // If URL has no sessionId, clear state if we have a session
    // 1. new session
    if (!urlSessionId) {
      if (sessionId) {
        dispatch({ type: "CLEAR_MESSAGES" });
        setSessionId(null);
        setHasStarted(false);
        setSessionBrandId(null);
        setSessionBrandKnown(false);
        setAiMode(DEFAULT_MODE);
        setAgent(null);
        setPendingConfirmation(null);
        setRegenerateStep(null);
        setShouldScrollToBottom(true);
        setOpenChangeModeConfirmationAlert(false);

        if (changedAiMode) {
          setAiMode(changedAiMode);
        }

        reduxDispatch(setChangedAIMode(""));
        reduxDispatch(resetPreviewState());
      }
      return;
    }

    // If URL sessionId matches current, do nothing
    // 2. same session
    if (urlSessionId === sessionId) {
      return;
    }

    // different session
    if (urlSessionId && !sessionId) {
      setSessionId(urlSessionId);
    }

    // Load the session from URL
    const loadSessionFromUrl = async () => {
      try {
        const sessionData = await getSession(urlSessionId);

        const loadedBrandId =
          sessionData?.metadata?.brand_id ||
          sessionData?.metadata?.brandId ||
          null;
        const normalizedLoadedBrandId = loadedBrandId
          ? String(loadedBrandId)
          : null;

        setAiMode(resolveModeFromSession(sessionData));

        // Transform messages to frontend format
        const messages = (sessionData.messages || []).map((msg) => {
          const blocks = msg.blocks?.length
            ? transformBlocksToFrontend(msg.blocks)
            : msg.content
              ? transformBlocksToFrontend([{ kind: "markdown", text: msg.content }])
              : [];

          // Restore attachment metadata from backend metadata_json
          const msgMeta = msg.metadata_json || msg.metadata || {};
          const attachments = msgMeta.attachments || undefined;
          if (attachments) {
            console.log(
              "[Session Load] Restored attachments for message:",
              msg.content?.substring(0, 30),
              attachments,
            );
          }

          return {
            id: msg.id || crypto.randomUUID(),
            role: msg.role,
            content: msg.content,
            blocks,
            agentName: msg.agent_name,
            attachments,
          };
        });

        let nextMessages = [...messages];

        // workflow ? — prefer the unique instance UUID so the GET
        // /workflows/{id} lookup always returns the correct instance even
        // when multiple campaigns share the same template.
        const workflowId =
          sessionData?.metadata?.workflow_instance_id ||
          sessionData?.metadata?.workflow_state?.workflow_id ||
          null;
        setWorkflowId(workflowId);
        // Track campaign_id for workflow attachments
        const loadedCampaignId = sessionData?.metadata?.campaign_id || null;
        setWorkflowCampaignId(
          loadedCampaignId ? String(loadedCampaignId) : null,
        );
        if (workflowId) {
          reduxDispatch(
            setData({
              workflow: {
                id: workflowId,
              },
            }),
          );
          // reduxDispatch(setPreviewOpen(true));
          reduxDispatch(setType("workflow"));
        } else {
          reduxDispatch(resetPreviewState());
        }

        // hitl confirmations ?
        setHitlConfirmations(sessionData?.pending_confirmations);

        setSessionId(urlSessionId);

        setSessionBrandId(normalizedLoadedBrandId);
        setSessionBrandKnown(true);
        setShouldScrollToBottom(true);

        dispatch({ type: "SET_MESSAGES", payload: nextMessages });
        setHasStarted(nextMessages.length > 0);
      } catch (err) {
        console.error("Failed to load session from URL:", err);
        router.replace("/ai-mode", undefined, { shallow: true });
      }
    };

    loadSessionFromUrl();
  }, [router.query.sessionId]);

  useEffect(() => {
    if (!sessionId || !sessionBrandKnown) {
      return;
    }
    const selectedId = selectedBrandId ? String(selectedBrandId) : null;
    const normalizedSessionBrandId = sessionBrandId
      ? String(sessionBrandId)
      : null;
    if (
      !selectedId ||
      !normalizedSessionBrandId ||
      normalizedSessionBrandId !== selectedId
    ) {
      startNewConversation();
    }
  }, [selectedBrandId, sessionId, sessionBrandId, sessionBrandKnown]);

  const addBotMessage = useCallback((msg) => {
    if (!hasStarted) {
      setHasStarted(true);
    }
    dispatch({ type: "ADD_MSG_ASSISTANT", payload: msg });
  }, []);

  const generateOutput = useCallback(
    async (step, notes, toolDecision) => {
      try {
        setIsExecutingTask(true);
        const stepOutputRes = await generateStepOutput({
          sessionId: sessionId,
          stepId: step.id,
          options: {
            existingNotes: notes ? notes : "",
            toolDecision: toolDecision,
          },
        });

        
        const displayStepNumber = formatStepIdForMoreHumanReadable(step?.id);

        if (
          stepOutputRes?.generated_output &&
          stepOutputRes?.approval_required?.length > 0
        ) {
          // Plan-Then-Execute: content generated as preview + tools need approval
          trackTokens(stepOutputRes.total_tokens);

          const blocks = [
            {
              kind: "markdown",
              text: `Please follow these instructions to complete the task: ${displayStepNumber} ${step.title}\n\nReview the content below. When ready, approve to execute via your connected tools.`,
            },
            {
              kind: "component",
              name: "TaskOutput",
              props: {
                step: {
                  ...step,
                  step_output: stepOutputRes.generated_output,
                  summary: {
                    step_output: stepOutputRes.generated_output,
                  },
                  isInternal: true,
                },
              },
            },
            {
              kind: "component",
              name: "ToolApproval",
              props: {
                approvalRequired: stepOutputRes.approval_required,
                step: {
                  ...step,
                  step_output: stepOutputRes.generated_output,
                },
              },
            },
          ];

        

          const botMsg = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: ``,
            blocks,
            agentName: null,
            actions: [],
            suggestions: [],
            confirmation: null,
          };
          addBotMessage(botMsg);
          setRegenerateStep(null);
          return true;
        } else if (stepOutputRes?.generated_output) {
          // Track tokens to Dodo for billing
          trackTokens(stepOutputRes.total_tokens);

          // Show the output immediately in chat. The backend also persists
          // this as a ConversationMessageModel so it survives page refresh.
          const message =  step?.executor == "Manual" ?  `Please follow these instructions to complete the task: ${displayStepNumber} ${step.title}` : `Here is the generated output for the task: ${displayStepNumber} ${step.title}`

          const botMsg = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: message,
            blocks: [
              {
                kind: "markdown",
                text: message,
              },
              {
                kind: "component",
                name: "TaskOutput",
                props: {
                  step: {
                    ...step,
                    step_output: stepOutputRes.generated_output,
                    summary: {
                      step_output: stepOutputRes.generated_output,
                    },
                    isInternal: true,
                  },
                },
              },
            ],
            agentName: null,
            actions: [],
            suggestions: [],
            confirmation: null,
          };
          addBotMessage(botMsg);
          setRegenerateStep(null);
          return true;
        } else if (stepOutputRes?.execution_state && stepOutputRes?.content) {
          // Handle tool decision states (awaiting_tool_decision, awaiting_connection, etc.)
          const botMsg = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `Step ${displayStepNumber} ${step.title} requires action`,
            blocks: [
              {
                kind: "markdown",
                text: stepOutputRes.content,
              },
              {
                kind: "component",
                name: "ToolDecision",
                props: {
                  step: step,
                  missingTools: stepOutputRes.missing_tools || [],
                },
              },
            ],
            agentName: null,
            actions: [],
            suggestions: [],
            confirmation: null,
          };
          addBotMessage(botMsg);
          setRegenerateStep(null);
          return true;
        } else {
          const botErrMsg = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: "Error while generating output for a step",
            blocks: [
              {
                kind: "markdown",
                text: `Unable to generate a output for a step with id ${step.id}. Please retry after some time`,
              },
            ],
            agentName: null,
            actions: [],
            suggestions: [],
            confirmation: null,
          };
          addBotMessage(botErrMsg);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsExecutingTask(false);
      }
    },
    [sessionId],
  );

  const handleResumeWorkflow = useCallback(async () => {
    setIsLoading(true);
    try {
      const resumeWorkflowResp = await resumeWorkflow(sessionId);
      if (resumeWorkflowResp?.status == "in_progress") {
        const payload = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Workflow has been resumed successfully!",
          blocks: [
            {
              kind: "markdown",
              text: `Workflow has been resumed successfully!`,
            },
          ],
          agentName: null,
          actions: [],
          suggestions: [],
          confirmation: null,
        };

        addBotMessage(payload);
        queryClient.invalidateQueries(["workflow"]);
      } else {
        const payload = {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            "Unable to resume the workflow. Try entering 'resume workflow'",
          blocks: [
            {
              kind: "markdown",
              text: `Unable to resume the workflow. Try entering 'resume workflow'`,
            },
          ],
          agentName: null,
          actions: [],
          suggestions: [],
          confirmation: null,
        };

        addBotMessage(payload);
      }
    } catch (error) {
      console.log(error, "error when resuming workflow");
      const payload = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "Unable to resume the workflow. Try entering 'resume workflow'",
        blocks: [
          {
            kind: "markdown",
            text: `Unable to resume the workflow. Try entering 'resume workflow'`,
          },
        ],
        agentName: null,
        actions: [],
        suggestions: [],
        confirmation: null,
      };

      addBotMessage(payload);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  const handleOpenWorkflow = useCallback(
    async (campaignId, shouldToggleTask, campaignWorkflowLookup) => {
      if (!campaignId) {
        toast.error("Campaign not found for this task.");
        return;
      }

      try {
        let workflowLookup;

        if (campaignWorkflowLookup) {
          workflowLookup = campaignWorkflowLookup;
        } else {
          workflowLookup = await getWorkflowByCampaign(campaignId);
        }

        // If no workflow exists, create one from the campaign's template
        if (!workflowLookup?.found) {
          toast("Setting up AI workflow for this campaign...");
          const createResponse = await createWorkflowFromCampaign(campaignId);
          if (createResponse?.session_id) {
            queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
            queryClient.invalidateQueries({
              queryKey: ["campaign-workflow-lookup", campaignId],
            });
            // Pre-set workflowId so useWorkflow can start fetching immediately
            // once the session loads.
            if (createResponse.workflow_instance_id) {
              setWorkflowId(String(createResponse.workflow_instance_id));
            }
            if (shouldToggleTask) reduxDispatch(toggleModal());
            router.push(
              `/ai-mode?sessionId=${createResponse.session_id}`,
              undefined,
              {
                shallow: true,
              },
            );
            return;
          }
          toast.error("Failed to create workflow for this campaign.");
          return;
        }

        let targetSessionId = workflowLookup?.user_session_id || null;
        if (!targetSessionId) {
          const joinResponse = await joinCampaignWorkflow(campaignId);
          targetSessionId = joinResponse?.session_id || null;
        }

        if (!targetSessionId) {
          toast.error("Unable to resolve workflow session.");
          return;
        }

        queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
        if (shouldToggleTask) reduxDispatch(toggleModal());
        router.push(`/ai-mode?sessionId=${targetSessionId}`, undefined, {
          shallow: true,
        });
      } catch (error) {
        console.error("Failed to open workflow from task", error);
        toast.error(
          error?.message || "Unable to open workflow for this campaign.",
        );
      }
    },
    [router],
  );

  const handleExecuteCurrentStep = useCallback(async () => {
    if (workflowData?.status === "paused") {
      await handleResumeWorkflow();
      return;
    }

    const lastEl = messages.at(-1);

    if (
      lastEl?.blocks?.[1]?.kind == "component" &&
      lastEl?.blocks?.[1]?.name == "TaskExecutionConfirmation"
    ) {
      return;
    }

    // Try workflowData first, fallback to the latest WorkflowForm message props
    let step_cards = workflowData?.step_cards;
    let current_step = workflowData?.current_step;

    if (!current_step || !step_cards?.phases) {
      for (let i = messages.length - 1; i >= 0; i--) {
        const blocks = messages[i].blocks || [];
        for (const block of blocks) {
          if (
            (block.kind === "ui_component" || block.kind === "component") &&
            block.name === "WorkflowForm"
          ) {
            const props = block.props || {};
            if (!current_step)
              current_step = props.step_id || props.current_step;
            if (!step_cards?.phases) step_cards = props.step_cards;
            break;
          }
        }
        if (current_step && step_cards?.phases) break;
      }
    }

    if (!current_step) return;

    const currentPhase = +current_step.split(":")[0] + 1;
    const currentGoal = +current_step.split(":")[1] + 1;

    const phase = step_cards?.phases?.find(
      (p) => p.phase_number == currentPhase,
    );

    const goal = phase?.goals?.find((g) => g.goal_number == currentGoal);

    const step = goal?.steps?.find((s) => s.id == current_step);

    if (!step) return;

    const payload = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      blocks: [
        {
          kind: "markdown",
          text: "Do you want to execute this Step ?",
        },
        {
          kind: "component",
          name: "TaskExecutionConfirmation",
          props: {
            step: step,
          },
        },
      ],
      agentName: null,
      actions: [],
      suggestions: [],
      confirmation: null,
    };

    addBotMessage(payload);
  }, [workflowData, messages, isWorkflowLoading]);

  useEffect(() => {
    if (workflowData && !isWorkflowLoading) {
      setWorkflowStatus(workflowData?.status || "");
    } else {
      setWorkflowStatus("");
    }
  }, [isWorkflowLoading, workflowData]);

  const values = useMemo(
    () => ({
      messages,
      handleMsgSubmit,
      handleMessageSubmit: handleMsgSubmit,
      hasStarted,
      setHasStarted,
      isPending: isPending || isResponding,
      pendingStatusMessage,
      isTranscribing,
      sessionId,
      mode: aiMode,
      setMode,
      agent,
      setAgent,
      pendingConfirmation,
      handleConfirmationResponse,
      startNewConversation,
      loadSession,
      error,
      input,
      setInput,
      aiMode,
      addBotMessage,
      setRegenerateStep,
      regenerateStep,
      isLoading,
      setIsLoading,
      isBlocked,
      generateOutput,
      isGeneratingOutput,
      isResumingWorkflow,
      handleResumeWorkflow,
      workflowStatus,
      hitlConfirmations,
      shouldScrollToBottom,
      handleOpenWorkflow,
      openChangeModeConfirmationAlert,
      setOpenChangeModeConfirmationAlert,
      handleExecuteCurrentStep,
      isWorkflowLoading,
      workflowData,
      isExecutingTask
    }),
    [
      messages,
      handleMsgSubmit,
      hasStarted,
      isPending,
      pendingStatusMessage,
      isResponding,
      isTranscribing,
      sessionId,
      aiMode,
      setMode,
      agent,
      setAgent,
      pendingConfirmation,
      handleConfirmationResponse,
      startNewConversation,
      loadSession,
      error,
      input,
      setInput,
      regenerateStep,
      isLoading,
      isBlocked,
      isGeneratingOutput,
      isResumingWorkflow,
      workflowStatus,
      isWorkflowLoading,
      isExecutingTask
    ],
  );

  return (
    <AssistanceContext.Provider value={values}>
      {children}
    </AssistanceContext.Provider>
  );
}

function messageReducer(state, action) {
  switch (action.type) {
    case "ADD_MSG_USER": {
      return [...state, action.payload];
    }
    case "ADD_MSG_ASSISTANT": {
      return [...state, action.payload];
    }
    case "UPDATE_LAST_MESSAGE": {
      if (state.length === 0) return state;
      const updatedMessages = [...state];
      updatedMessages[updatedMessages.length - 1] = {
        ...updatedMessages[updatedMessages.length - 1],
        ...action.payload,
      };
      return updatedMessages;
    }
    case "CLEAR_MESSAGES": {
      return [];
    }
    case "SET_MESSAGES": {
      return action.payload;
    }
    default:
      return state;
  }
}

/**
 * Message reducer for state management
 */

/**
 * Custom hook to access the Assistance context
 */
export const useAssistance = () => {
  const context = useContext(AssistanceContext);
  if (!context) {
    throw new Error("useAssistance must be used within an AssistanceProvider");
  }
  return context;
};
